import { tabsListVariants, tabsTriggerVariants } from '@platform/ui';
import { Link, useRouterState } from '@tanstack/react-router';

import { useMessages } from '../i18n';
import { useCanOpen } from '../lib/session';

/**
 * Routes that are sections of one job, shown as the tabs of one page (docs/11 §8).
 *
 * Each tab keeps its own address, so a reload, a shared link and the back button
 * land on the tab that was open, while the menu carries a single entry for the
 * whole job — which is what keeps it short enough to read without scrolling. The
 * menu entry leads to the first tab the person may open; what each tab needs is
 * its route's permission.
 */
export const sections = {
    stock: {
        titleKey: 'inventory',
        tabs: [
            { to: '/stok', labelKey: 'inventory' },
            { to: '/depolar', labelKey: 'warehouses' },
        ],
    },
    mapping: {
        titleKey: 'mapping',
        tabs: [
            { to: '/kategori-eslestirme', labelKey: 'categories' },
            { to: '/urun-eslestirme', labelKey: 'products' },
            { to: '/marka-eslestirme', labelKey: 'brandMapping' },
        ],
    },
    finance: {
        titleKey: 'finance',
        tabs: [
            { to: '/finans', labelKey: 'settlements' },
            { to: '/karlilik', labelKey: 'profitability' },
            { to: '/urun-performansi', labelKey: 'productPerformance' },
            { to: '/satis-trendi', labelKey: 'salesTrend' },
        ],
    },
    developer: {
        titleKey: 'developer',
        tabs: [
            { to: '/webhooks', labelKey: 'webhooks' },
            { to: '/api-erisimi', labelKey: 'apiTokens' },
        ],
    },
} as const;

/** Whether the address is the route or a page under it: /urunler holds /urunler/yeni. */
export function onRoute(pathname: string, to: string): boolean {
    return to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`);
}

/** The tab row under a section page's header. A single permitted tab is no choice, so it renders nothing. */
export function SectionTabs({ section }: { section: keyof typeof sections }) {
    const t = useMessages();
    const canOpen = useCanOpen();
    const pathname = useRouterState({ select: (state) => state.location.pathname });

    const tabs = sections[section].tabs.filter((tab) => canOpen(tab.to));
    if (tabs.length < 2) return null;

    return (
        // Links in a nav rather than role="tab": each tab is a page of its own,
        // and a tablist promises panels that switch in place. The line is the
        // shared tab strip's, so the two read as one pattern.
        <nav className={tabsListVariants()} aria-label={t.nav[sections[section].titleKey]}>
            {tabs.map((tab) => (
                <Link
                    key={tab.to}
                    to={tab.to}
                    className={tabsTriggerVariants()}
                    aria-current={onRoute(pathname, tab.to) ? 'page' : undefined}
                    activeOptions={{ exact: true }}
                >
                    {t.nav[tab.labelKey]}
                </Link>
            ))}
        </nav>
    );
}
